import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/integrations/supabase/client'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { useToast } from '@/hooks/use-toast'
import { cn } from '@/lib/utils'
import { CheckCircle2, AlertTriangle, XCircle, Plus, Loader2 } from 'lucide-react'

const COMPONENTS = [
  'Authentication',
  'Database',
  'Edge Functions',
  'Storage',
  'Realtime',
  'Email Delivery',
  'Social Media APIs',
]

const STATUS_META: Record<string, { label: string; badge: string; icon: any }> = {
  healthy:  { label: 'Healthy',  badge: 'bg-green-100 text-green-700',  icon: CheckCircle2 },
  degraded: { label: 'Degraded', badge: 'bg-amber-100 text-amber-700',  icon: AlertTriangle },
  down:     { label: 'Down',     badge: 'bg-red-100 text-red-700',      icon: XCircle },
}

export function SystemHealthView() {
  const { toast } = useToast()
  const qc = useQueryClient()
  const [reportOpen, setReportOpen] = useState(false)
  const [form, setForm] = useState({ component: COMPONENTS[0], status: 'degraded', message: '' })
  const [submitting, setSubmitting] = useState(false)

  const { data: healthLog } = useQuery({
    queryKey: ['system-health'],
    queryFn: async () => {
      const { data } = await supabase
        .from('system_health_log')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(50)
      return data || []
    },
    refetchInterval: 30000,
  })

  // Latest entry per component drives the status card + overall banner
  const latestByComponent: Record<string, any> = {}
  ;(healthLog ?? []).forEach(entry => {
    if (!latestByComponent[entry.component]) latestByComponent[entry.component] = entry
  })

  const statuses = COMPONENTS.map(c => latestByComponent[c]?.status ?? 'healthy')
  const overall = statuses.includes('down') ? 'down' : statuses.includes('degraded') ? 'degraded' : 'healthy'

  const OVERALL_META: Record<string, { label: string; classes: string }> = {
    healthy:  { label: 'All Systems Operational', classes: 'bg-green-50 border-green-200 text-green-700' },
    degraded: { label: 'Degraded Performance',    classes: 'bg-amber-50 border-amber-200 text-amber-700' },
    down:     { label: 'Outage',                  classes: 'bg-red-50 border-red-200 text-red-700' },
  }

  async function submitReport() {
    if (!form.message.trim()) return
    setSubmitting(true)
    try {
      await supabase.from('system_health_log').insert({
        component: form.component,
        status: form.status,
        message: form.message.trim(),
      })
      toast({ title: 'Issue reported.' })
      setForm({ component: COMPONENTS[0], status: 'degraded', message: '' })
      setReportOpen(false)
      qc.invalidateQueries({ queryKey: ['system-health'] })
    } catch (err: any) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">System Health</h1>
          <p className="text-justify text-muted-foreground text-sm mt-1">Live status of CONORBIT platform components.</p>
        </div>
        <Button size="sm" variant="outline" className="gap-1.5 shrink-0" onClick={() => setReportOpen(true)}>
          <Plus className="w-3.5 h-3.5" />
          Report Issue
        </Button>
      </div>

      {/* Overall status banner */}
      <div className={cn('rounded-lg border p-4 flex items-center gap-2 font-medium text-sm', OVERALL_META[overall].classes)}>
        {overall === 'healthy' ? <CheckCircle2 className="w-5 h-5" /> : overall === 'degraded' ? <AlertTriangle className="w-5 h-5" /> : <XCircle className="w-5 h-5" />}
        {OVERALL_META[overall].label}
      </div>

      {/* Component cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {COMPONENTS.map(component => {
          const entry = latestByComponent[component]
          const status = entry?.status ?? 'healthy'
          const meta = STATUS_META[status] ?? STATUS_META.healthy
          const Icon = meta.icon
          return (
            <Card key={component}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-muted-foreground">{component}</span>
                  <Icon className={cn('w-4 h-4', status === 'healthy' ? 'text-green-600' : status === 'degraded' ? 'text-amber-600' : 'text-red-600')} />
                </div>
                <Badge className={cn('text-[10px]', meta.badge)}>{meta.label}</Badge>
                <p className="text-[11px] text-muted-foreground mt-2 truncate">
                  {entry?.message ?? 'No recent issues'}
                </p>
                <p className="text-justify text-[10px] text-muted-foreground mt-1">
                  {entry ? new Date(entry.created_at).toLocaleString() : ''}
                </p>
              </CardContent>
            </Card>
          )
        })}
      </div>

      {/* Health log table */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Recent Health Log</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/30 text-left">
                <th className="px-4 py-2.5 font-medium text-muted-foreground">Timestamp</th>
                <th className="px-2 py-2.5 font-medium text-muted-foreground">Component</th>
                <th className="px-2 py-2.5 font-medium text-muted-foreground">Status</th>
                <th className="px-2 py-2.5 font-medium text-muted-foreground">Message</th>
                <th className="px-2 py-2.5 font-medium text-muted-foreground">Resolved</th>
              </tr>
            </thead>
            <tbody>
              {(healthLog ?? []).length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">No health log entries yet.</td>
                </tr>
              ) : (healthLog ?? []).slice(0, 20).map(entry => (
                <tr key={entry.id} className="border-b last:border-0">
                  <td className="px-4 py-2.5 text-muted-foreground text-xs whitespace-nowrap">
                    {new Date(entry.created_at).toLocaleString()}
                  </td>
                  <td className="px-2 py-2.5">{entry.component}</td>
                  <td className="px-2 py-2.5">
                    <Badge className={cn('text-[10px]', (STATUS_META[entry.status] ?? STATUS_META.healthy).badge)}>
                      {(STATUS_META[entry.status] ?? STATUS_META.healthy).label}
                    </Badge>
                  </td>
                  <td className="px-2 py-2.5 text-muted-foreground max-w-xs truncate">{entry.message ?? ''}</td>
                  <td className="px-2 py-2.5 text-muted-foreground text-xs">
                    {entry.resolved_at ? new Date(entry.resolved_at).toLocaleDateString() : ''}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Report Issue sheet */}
      <Sheet open={reportOpen} onOpenChange={setReportOpen}>
        <SheetContent side="right" className="w-[420px]">
          <SheetHeader>
            <SheetTitle>Report Issue</SheetTitle>
          </SheetHeader>
          <div className="mt-5 space-y-4">
            <div className="space-y-1.5">
              <Label>Component</Label>
              <Select value={form.component} onValueChange={v => setForm(f => ({ ...f, component: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {COMPONENTS.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Status</Label>
              <Select value={form.status} onValueChange={v => setForm(f => ({ ...f, status: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="degraded">Degraded</SelectItem>
                  <SelectItem value="down">Down</SelectItem>
                  <SelectItem value="healthy">Healthy (resolved)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Message</Label>
              <Textarea
                rows={4}
                value={form.message}
                onChange={e => setForm(f => ({ ...f, message: e.target.value }))}
                placeholder="Describe the issue…"
                className="resize-none"
              />
            </div>
            <Button className="w-full" disabled={submitting || !form.message.trim()} onClick={submitReport}>
              {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Submit Report
            </Button>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  )
}
